import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/Chart";
import { QUERY_KEY } from "@/constant/queryKey";
import { fetchDashboardData } from "@/services/api-service";
import {
  ADMIN_DASHBOARD_ENDPOINT,
  AdminDashboardAreaData,
} from "@/services/core/admin-dashboard-charts.api";
import { useDashboardStore } from "@/store/dashboard-store";
import { useMerchantStore } from "@/store/merchant-store";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { TransactionAreaChartSkeleton } from "./TransactionAreaChartSkeleton";

const areaChartConfig = {
  success: {
    label: "Successful",
    color: "hsl(142.1 76.2% 36.3%)", // Green
  },
  failed: {
    label: "Failed",
    color: "hsl(346.8 77.2% 49.8%)", // Red
  },
} satisfies ChartConfig;

const TransactionAreaChart = () => {
  const { selectedMerchant } = useMerchantStore();
  const { dateRange } = useDashboardStore();

  const endPoint = `${ADMIN_DASHBOARD_ENDPOINT}/${selectedMerchant?.name}/area-data`;
  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEY.ADMIN_DASHBOARD_AREA,
      selectedMerchant?.name,
      dateRange,
    ],
    queryFn: () =>
      fetchDashboardData<AdminDashboardAreaData>(endPoint, {
        username: selectedMerchant?.name ?? "",
        from: dateRange.from,
        to: dateRange.to,
      }),
  });

  const areaChartData = useMemo(() => {
    if (!data?.payload) return [];

    return data.payload.map((el) => {
      const totals = Object.values(el.channels).reduce(
        (acc, channel) => ({
          success: acc.success + channel.success,
          failed: acc.failed + channel.failed,
        }),
        {
          success: 0,
          failed: 0,
        },
      );

      return {
        month: el.month,
        success: totals.success,
        failed: totals.failed,
      };
    });
  }, [data]);

  if (isLoading) {
    return <TransactionAreaChartSkeleton />;
  }

  if (!data?.payload?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Transaction Success Analysis</CardTitle>
          <CardDescription>
            Success vs Failed transactions over 6 months
          </CardDescription>
        </CardHeader>
        <CardContent className="flex h-[400px] items-center justify-center px-20">
          <div className="text-center">
            <p className="text-muted-foreground text-sm">
              No transaction data available for the selected date range
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Transaction Success Analysis</CardTitle>
        <CardDescription>
          Success vs Failed transactions over 6 months
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          className="mt-10 max-h-[400px]"
          config={areaChartConfig}
        >
          <AreaChart data={areaChartData} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickFormatter={(value) => value.slice(0, 3)}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              tickFormatter={(value) => `${value.toLocaleString()}`}
            />
            <ChartTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const total = payload.reduce(
                    (acc, entry) => acc + (entry.value as number),
                    0,
                  );
                  return (
                    <div className="bg-background rounded-lg border p-2 shadow-sm">
                      <div className="grid gap-2">
                        <div className="flex flex-col">
                          <span className="text-muted-foreground text-[0.70rem] uppercase">
                            Month
                          </span>
                          <span className="font-bold">
                            {payload[0].payload.month}
                          </span>
                        </div>
                        {payload.map((entry) => (
                          <div
                            key={entry.name}
                            className="flex justify-between gap-2"
                          >
                            <span className="text-muted-foreground text-[0.70rem] uppercase">
                              {entry.name}
                            </span>
                            <span className="font-bold">
                              {entry.value?.toLocaleString()} (
                              {(
                                ((entry.value as number) / total) *
                                100
                              ).toFixed(1)}
                              %)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              dataKey="success"
              stackId="transactions"
              type="monotone"
              fill="#22c55e"
              fillOpacity={0.4}
              stroke="#22c55e"
            />
            <Area
              dataKey="failed"
              stackId="transactions"
              type="monotone"
              fill="#ef4444"
              fillOpacity={0.4}
              stroke="#ef4444"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
      <CardFooter>
        <div className="flex w-full items-start gap-2 text-sm md:mt-20 xl:mt-0">
          <div className="grid w-full gap-5">
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-[#22c55e] opacity-40" />
                <span className="text-muted-foreground text-xs">
                  Successful
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-[#ef4444] opacity-40" />
                <span className="text-muted-foreground text-xs">Failed</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 leading-none font-medium">
                Success rate improving by 2.5% this month{" "}
                <TrendingUp className="h-4 w-4" />
              </div>
              <div className="text-muted-foreground flex items-center gap-2 leading-none">
                January - June 2024
              </div>
            </div>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default TransactionAreaChart;
